import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, CheckCircle2, Clock, LogIn, LogOut } from "lucide-react";
import api from "@/api";
import GtLogo from "@/components/GtLogo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Phase F (Feb 2026): Mandatory check-in / check-out gate for salesman + manager.
// App access is blocked until check-in is done today.
export default function Attendance() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const [rec, setRec] = useState(undefined); // undefined = loading, null = not checked-in
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get("/attendance/today")
      .then(r => setRec(r.data))
      .catch(() => setRec(null));
  };
  useEffect(() => { refresh(); }, []);

  // If salesman/manager already checked-in (and not yet checked-out), redirect to home
  useEffect(() => {
    if (rec && rec.check_in_at && !rec.check_out_at) {
      const home = user?.role === "manager" ? "/manager" : "/salesman";
      nav(home, { replace: true });
    }
  }, [rec, user, nav]);

  const captureGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const t = setTimeout(() => resolve(null), 1500);
    navigator.geolocation.getCurrentPosition(
      (pos) => { clearTimeout(t); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }); },
      () => { clearTimeout(t); resolve(null); },
      { enableHighAccuracy: false, timeout: 1500 }
    );
  });

  const checkIn = async () => {
    setBusy(true);
    try {
      const geo = await captureGeo();
      const r = await api.post("/attendance/check-in", { geo });
      setRec(r.data);
      toast.success(lang === "ta" ? "வேலை துவங்கியாச்சு" : "Day started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const checkOut = async () => {
    setBusy(true);
    try {
      const geo = await captureGeo();
      const r = await api.post("/attendance/check-out", { geo });
      setRec(r.data);
      toast.success(lang === "ta" ? "வேலை முடிஞ்சாச்சு" : "Day ended");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  if (rec === undefined) {
    return <div className="min-h-screen bg-[#F5F5F7] grid place-items-center"><div className="text-[#86868B]">{t("loading")}</div></div>;
  }

  const checkedIn = !!(rec && rec.check_in_at);
  const checkedOut = !!(rec && rec.check_out_at);

  // Compute elapsed
  let elapsed = "—";
  if (checkedIn && !checkedOut) {
    const ci = new Date(rec.check_in_at);
    const mins = Math.max(Math.floor((Date.now() - ci.getTime()) / 60000), 0);
    elapsed = `${Math.floor(mins/60)}h ${mins%60}m`;
  } else if (checkedOut && rec.total_minutes != null) {
    elapsed = `${Math.floor(rec.total_minutes/60)}h ${rec.total_minutes%60}m`;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1A3F] via-[#0055FF] to-[#5856D6] grid place-items-center p-5">
      <div className="bg-white rounded-3xl shadow-2xl p-7 sm:p-9 w-full max-w-md fade-up">
        <div className="flex flex-col items-center text-center mb-5">
          <GtLogo size="w-20 h-20" className="mb-3" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#86868B] font-bold">
            {lang === "ta" ? "வரவு பதிவு" : "Attendance"}
          </div>
          <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">
            {lang === "ta" ? `வணக்கம், ${user?.name || ""}` : `Hello, ${user?.name || ""}`}
          </div>
        </div>

        {!checkedIn ? (
          <>
            <div className="bg-blue-50 rounded-2xl p-5 text-center mb-5">
              <Sun className="w-10 h-10 mx-auto text-[#FF9500] mb-2" />
              <div className="heading-font font-bold text-lg text-[#1D1D1F]">
                {lang === "ta" ? "உங்க வேலை நாளை துவங்குங்க" : "Start Your Working Day"}
              </div>
              <div className="text-sm text-[#86868B] mt-1.5">
                {lang === "ta"
                  ? "ஆப் பயன்படுத்த முதல் வரவு பதிவு செய்யுங்க"
                  : "Tap below to check-in. App access is enabled after check-in."}
              </div>
            </div>
            <button
              data-testid="att-check-in"
              onClick={checkIn}
              disabled={busy}
              className="w-full bg-[#34C759] hover:bg-[#2EAF4E] disabled:opacity-50 text-white rounded-2xl py-4 text-lg font-bold flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition"
            >
              <LogIn className="w-5 h-5" />
              {lang === "ta" ? "வேலை துவங்கு" : "Check-In"}
            </button>
          </>
        ) : !checkedOut ? (
          <>
            <div className="bg-green-50 rounded-2xl p-5 text-center mb-4">
              <CheckCircle2 className="w-10 h-10 mx-auto text-[#34C759] mb-2" />
              <div className="heading-font font-bold text-lg text-[#1D1D1F]">
                {lang === "ta" ? "நாள் ஆரம்பம் ஆச்சு" : "Day Started"}
              </div>
              <div className="text-xs text-[#86868B] mt-1">{lang === "ta" ? "நேரம்" : "At"}: {new Date(rec.check_in_at).toLocaleTimeString()}</div>
              <div className="text-xs text-[#86868B] flex items-center justify-center gap-1 mt-1"><Clock className="w-3 h-3" /> {elapsed}</div>
            </div>
            <button
              data-testid="att-go-home"
              onClick={() => nav(user?.role === "manager" ? "/manager" : "/salesman")}
              className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-2xl py-3.5 font-bold mb-2"
            >
              {lang === "ta" ? "ஆப் திற" : "Open App"} →
            </button>
            <button
              data-testid="att-check-out"
              onClick={checkOut}
              disabled={busy}
              className="w-full bg-white hover:bg-gray-50 border border-[#FF3B30] text-[#FF3B30] rounded-2xl py-3 font-bold flex items-center justify-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              {lang === "ta" ? "வேலை முடி" : "Check-Out"}
            </button>
          </>
        ) : (
          <>
            <div className="bg-purple-50 rounded-2xl p-5 text-center mb-4">
              <Moon className="w-10 h-10 mx-auto text-[#5856D6] mb-2" />
              <div className="heading-font font-bold text-lg text-[#1D1D1F]">{lang === "ta" ? "வேலை முடிஞ்சாச்சு" : "Day Completed"}</div>
              <div className="text-xs text-[#86868B] mt-1">
                {new Date(rec.check_in_at).toLocaleTimeString()} → {new Date(rec.check_out_at).toLocaleTimeString()}
              </div>
              <div className="text-xs text-[#86868B] flex items-center justify-center gap-1 mt-1"><Clock className="w-3 h-3" /> {elapsed}</div>
            </div>
            <button
              data-testid="att-logout"
              onClick={() => { logout(); nav("/login", { replace: true }); }}
              className="w-full bg-[#FF3B30] hover:bg-[#D6332B] text-white rounded-2xl py-3.5 font-bold"
            >
              {lang === "ta" ? "வெளியேறு" : "Sign out"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
